<script setup lang="ts">
import type { ListboxGroupProps } from 'reka-ui'
import { ListboxGroup, ListboxGroupLabel, useId } from 'reka-ui'
import { cn } from '@/lib/utils'
import { computed, type HTMLAttributes, onMounted, onUnmounted } from 'vue'
import { provideCommandGroupContext, useCommand } from '.'
import { LoaderIcon } from 'lucide-vue-next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { UpdateIcon } from '@radix-icons/vue'

const props = defineProps<
    ListboxGroupProps & {
        class?: HTMLAttributes['class']
        heading?: string
        loading?: boolean
        refreshable?: boolean
    }
>()

defineEmits<{
    refresh: []
}>()

const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props

    return delegated
})

const { allGroups, filterState } = useCommand()
const id = useId()

const isRender = computed(() => (!filterState.search ? true : filterState.filtered.groups.has(id)))

provideCommandGroupContext({ id })
onMounted(() => {
    if (!allGroups.value.has(id)) allGroups.value.set(id, new Set())
})
onUnmounted(() => {
    allGroups.value.delete(id)
})
</script>

<template>
    <ListboxGroup
        v-bind="delegatedProps"
        :id="id"
        :class="
            cn(
                'overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground',
                props.class
            )
        "
    >
        <ListboxGroupLabel
            v-if="heading"
            class="px-2 py-1.5 text-xs font-medium text-muted-foreground flex items-center justify-between"
        >
            <div>{{ heading }}</div>
            <TooltipProvider v-if="refreshable">
                <Tooltip>
                    <TooltipTrigger as-child>
                        <Button
                            variant="icon"
                            size="icon-sm"
                            class="grow-0 shrink-0"
                            :disabled="loading"
                            @click="$emit('refresh')"
                        >
                            <LoaderIcon v-if="loading" class="w-3 h-3 animate-spin" />
                            <UpdateIcon v-else class="h-3 w-3" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                        <p>Refresh</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </ListboxGroupLabel>
        <slot />
    </ListboxGroup>
</template>
